import React from 'react';
import { Language } from '@go-shared/types/info';
import { BubbleFormGroup } from './BubbleUI';

/* A translation block is composed of a label and an optional word count fn,
 * describing the source material to translate, and then, optionally, for every
 * language, children to render for that language and word count fn. For example:
 *
 * ====================================
 * TITLE (18) - Movie in the Manor
 * ------------------------------------
 * SPANISH (8) - Pelicula
 * {children}
 *
 * FRENCH (7) - Bonjour
 * {children}
 * ====================================
 *
 * Note that it does not take in translations! Only the languages to translate.
 * You provide what to render for each language via children.
 */
interface TranslationBlockProps {
  languages: Language[]; // e.g. ['es', 'fr']
  label: string; // e.g. TITLE
  value: string; // e.g. Movie in the Manor

  // render something other than "Movie in the Manor"
  headerValueFn?: (props: TranslationBlockProps) => React.ReactNode;
  // render something other than (len("Movie in the Manor"))
  headerCharCountFn?: (props: TranslationBlockProps) => React.ReactNode;

  // for each ['es', 'fr'], render CC next to language title (optional)
  charCountFn?: (props: TranslationBlockChildProps) => React.ReactNode;
  // for each ['es', 'fr'], render content (optional)
  children: (props: TranslationBlockChildProps) => React.ReactNode;

  className?: string;
  headerCharCountClassName?: string;
}

type TranslationBlockChildProps = TranslationBlockProps & {
  language: Language;
};

export default function TranslationBlock(props: TranslationBlockProps) {
  const {
    languages,
    label,
    value,
    headerCharCountFn,
    headerValueFn,
    charCountFn,
    className,
    headerCharCountClassName,
    children,
  } = props;

  const defaultCCFn = (val: string) => <span className="text-smoke-light">({val.length})</span>;
  const defaultValFn = (val: string) => <p>{val}</p>;

  const headCCFn = headerCharCountFn || (() => defaultCCFn(value));
  const headValFn = headerValueFn || (() => defaultValFn(value));

  const headerCharCountClasses: string[] = [];
  if (headerCharCountClassName) headerCharCountClasses.push(headerCharCountClassName);

  return (
    <div className={className}>
      <div className="bg-white border-b p-5">
        <BubbleFormGroup
          className="mb-3"
          labelClassName={headerCharCountClasses.join(' ')}
          labelFn={() => (
            <>
              <span className="mr-2">{label}</span>
              {headCCFn(props)}
            </>
          )}
        >
          {headValFn(props)}
        </BubbleFormGroup>
        <div className="p-5 bg-eggshell grid gap-4 border rounded shadow-sm">
          {languages.map(lang => (
            <BubbleFormGroup
              key={lang.key}
              labelFn={() => (
                <>
                  <span className="mr-2">
                    {lang.englishLabel} ({lang.nativeLabel})
                  </span>
                  {charCountFn && charCountFn({ ...props, language: lang })}
                </>
              )}
            >
              {children({ ...props, language: lang })}
            </BubbleFormGroup>
          ))}
          {languages.length < 1 && (
            <p className="text-smoke-light">No languages shown (check filters)</p>
          )}
        </div>
      </div>
    </div>
  );
}
